import { match } from 'ts-pattern';

/**
 * Finds the number of starting characters shared between two strings. E.g.
 * findStartOverlapCount('Hello', 'Hey') === 2 ('He')
 */
export function findStartOverlapCount(a: string, b: string): number {
  if (a.length === 0 || b.length === 0) {
    return 0;
  }

  let index = 0;
  while (index !== a.length && index !== b.length) {
    if (a[index] !== b[index]) {
      break;
    }
    index += 1;
  }
  return index;
}

export function assertString(arg: unknown): string {
  if (typeof arg === 'string') {
    return arg;
  }

  throw new Error('Not a string!');
}

export function assertStringOrUndefined(arg: unknown): string | undefined {
  if (typeof arg === 'string' || typeof arg === 'undefined') {
    return arg;
  }

  throw new Error('Not a string or undefined!');
}

export function getFullName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`;
}

/**
 * Trims a string to given max length.
 * @param str: The string to trim.
 * @param maxLength: The max length of the string.
 * @returns The updated trimmed string.
 */
export const trimStringToLength = (str: string | undefined, maxLength: number): string | undefined =>
  str ? str.substring(0, maxLength) : undefined;

/**
 * Capitalizes the first letter of the string.
 * @param str: The string to capitalize.
 * @returns The formatted string capitalized.
 */
export const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Capitalizes the first letter of each word in a string.
 * Use this function instead of lodash startCase (Which removes specials characters)
 * @param str: The string to proper case.
 * @returns The formatted string in proper case.
 */
export const properCase = (str: string): string => str.split(' ').map(capitalize).join(' ');

/**
 * Filters out non-printable characters from a string.
 *
 * According to the ASCII character encoding, there are 95 printable characters in total.
 * Those characters are in the range [0x20 to 0x7E] ([32 to 126] in decimal) and they represent letters, digits, punctuation marks, and a few miscellaneous symbols.
 * Character 0x20 (or 32 in decimal) is the space character ' ' and character 0x7E (or 126 in decimal) is the tilde character '~'.
 *
 * Since all the printable characters of ASCII are conveniently in one continuous range, we can use the following to filter all other characters out of our string in JavaScript.
 * `/[^ -~]+/g`
 *
 * @param str: The string to filter.
 * @returns The filtered string with non-printable characters removed.
 */
export const filterNonPrintableCharacters = (str: string): string => str.replace(/[^ -~]+/g, '');

/**
 * Formats a list of items in the following format:
 *
 * One item => item1
 * Two items => item1 and item2
 * Three or more items => item1, item2, and item3
 *
 * @param items the list of items to format
 * @param itemSeparator optional separator (and/or for example)
 * @returns the list of items formatted as a string
 */
export const oxfordJoin = (items: string[], itemSeparator = 'and'): string =>
  match(items.length)
    .with(0, () => '')
    .with(1, () => items[0])
    .with(2, () => `${items[0]} ${itemSeparator} ${items[1]}`)
    .otherwise(() =>
      items.reduce((acc, cur, index) =>
        index !== items.length - 1 ? `${acc}, ${cur}` : `${acc}, ${itemSeparator} ${cur}`
      )
    );

const PLURALIZE_WITH_ES_WORDS = ['lunch', 'dish'];

/**
 * Pluralizes a word based on the count.
 *
 * Note: This will only work for `s` case, not `es` or `ies`.
 *
 * @param word The word to pluralize.
 * @param count The count to pluralize by.
 * @returns The pluralized word.
 */
export const pluralize = (word: string, count: number): string => {
  if (count === 1) {
    return word;
  }
  if (PLURALIZE_WITH_ES_WORDS.includes(word.toLowerCase())) {
    return `${word}es`;
  }
  return `${word}s`;
};

/** Verb agreement for a count, the counterpart to {@link pluralize}: "1 order is", "2 orders are". */
export const isOrAre = (count: number): string => (count === 1 ? 'is' : 'are');
